import { useEffect, useId, useRef, useState } from 'react'
import {
    AdvancedMarker,
    ControlPosition,
    MapControl,
    Pin,
    useMap,
    useMapsLibrary,
} from '@vis.gl/react-google-maps'
import { Loader2, Search, X } from 'lucide-react'

import { PlaceSearchOptions } from './place-search-options'
import { parseLatLng } from './parse-lat-lng'
import { usePlacePredictions } from './use-place-predictions'
import { useSearchWidth } from './use-search-width'
import type { PlaceSearchOption } from './place-search-options'
import type { LatLng } from '@/components/maps/map-controllers'
import type { MapPlaceSearchEntry } from '@/lib/map-place-search-tracking'
import { captureMapPlaceSearch } from '@/lib/map-place-search-tracking'
import { m } from '@/lib/paraglide/messages'

/** Tới một điểm (toạ độ, hoặc địa điểm không có khung bao) thì zoom mức này. */
export const PLACE_ZOOM = 17

/**
 * Ô tìm vị trí trên bản đồ giống Google Maps: gõ tên địa điểm (gợi ý của Google)
 * hoặc dán toạ độ "vĩ độ, kinh độ". Chọn xong bản đồ bay tới đó và cắm một ghim
 * tạm; ghim chỉ để đánh dấu, KHÔNG phải điểm ranh giới thửa. Đặt trong `<Map>`;
 * dùng MapControl nên vẫn hiện khi bản đồ toàn màn hình.
 */
export function MapPlaceSearch({ entry }: { entry: MapPlaceSearchEntry }) {
    const map = useMap()
    const places = useMapsLibrary('places')
    const listId = useId()
    const width = useSearchWidth(map)
    const [query, setQuery] = useState('')
    const [open, setOpen] = useState(false)
    const [active, setActive] = useState(0)
    const [pin, setPin] = useState<LatLng | null>(null)
    const [detailError, setDetailError] = useState(false)
    // Mỗi phiên tìm (gõ → chọn/xoá) chỉ ghi `no_results`/`error` một lần;
    // gõ thêm ký tự KHÔNG mở phiên mới.
    const reportedRef = useRef(false)
    // Enter khi gợi ý của chuỗi đang gõ chưa về: đợi về rồi chọn dòng đầu.
    const pendingEnterRef = useRef(false)
    // Người dùng đã chủ động chọn dòng bằng phím mũi tên từ lần gõ cuối.
    const navigatedRef = useRef(false)

    const coords = parseLatLng(query)
    const predictions = usePlacePredictions(query, open && !coords)
    const options: PlaceSearchOption[] = coords
        ? [{ kind: 'coords', position: coords }]
        : predictions.predictions.map((p) => ({ kind: 'place', ...p }))

    // Gợi ý (hoặc lỗi) thuộc đúng chuỗi đang gõ, không phải chuỗi cũ còn chờ
    // debounce.
    const settled =
        predictions.query === query.trim() &&
        (predictions.status === 'ok' || predictions.status === 'error')
    const noResults =
        predictions.status === 'ok' && predictions.predictions.length === 0
    const failure =
        coords || !settled
            ? null
            : predictions.status === 'error'
              ? 'error'
              : noResults
                ? 'no_results'
                : null
    const report = (outcome: 'no_results' | 'error') => {
        if (reportedRef.current) return
        reportedRef.current = true
        captureMapPlaceSearch({ entry, outcome })
    }
    useEffect(() => {
        if (!failure || reportedRef.current) return
        reportedRef.current = true
        captureMapPlaceSearch({ entry, outcome: failure })
    }, [failure, entry])

    useEffect(() => {
        if (!pendingEnterRef.current || (!coords && !settled)) return
        pendingEnterRef.current = false
        const first = options[0]
        if (first) choose(first)
    })

    const goTo = (position: LatLng, viewport?: google.maps.LatLngBounds) => {
        setPin(position)
        setOpen(false)
        reportedRef.current = false
        if (!map) return
        if (viewport) {
            map.fitBounds(viewport)
            return
        }
        map.panTo(position)
        map.setZoom(PLACE_ZOOM)
    }

    const choose = (option: PlaceSearchOption) => {
        setDetailError(false)
        if (option.kind === 'coords') {
            captureMapPlaceSearch({ entry, outcome: 'coords' })
            goTo(option.position)
            return
        }
        if (!places || !map) return
        setQuery(option.mainText)
        setOpen(false)
        const sessionToken = predictions.getSessionToken()
        new places.PlacesService(map).getDetails(
            {
                placeId: option.placeId,
                fields: ['geometry'],
                ...(sessionToken ? { sessionToken } : {}),
            },
            (place, status) => {
                predictions.resetSession()
                const loc = place?.geometry?.location
                if (status !== 'OK' || !loc) {
                    setDetailError(true)
                    report('error')
                    return
                }
                captureMapPlaceSearch({ entry, outcome: 'place' })
                goTo(
                    { lat: loc.lat(), lng: loc.lng() },
                    place.geometry?.viewport ?? undefined,
                )
            },
        )
    }

    const clear = () => {
        pendingEnterRef.current = false
        setQuery('')
        setPin(null)
        setOpen(false)
        setDetailError(false)
        reportedRef.current = false
        predictions.resetSession()
    }

    const showList = open && query.trim() !== ''

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            pendingEnterRef.current = false
            setOpen(false)
            return
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            setOpen(true)
            navigatedRef.current = true
            pendingEnterRef.current = false
            const step = e.key === 'ArrowDown' ? 1 : -1
            setActive((i) =>
                options.length === 0
                    ? 0
                    : (i + step + options.length) % options.length,
            )
            return
        }
        if (e.key === 'Enter') {
            e.preventDefault()
            // Dòng đang hiện là gợi ý của chuỗi cũ (chưa hết debounce) thì Enter
            // sẽ bay nhầm chỗ — trừ khi người dùng đã chủ động chọn dòng đó.
            // Danh sách đang đóng (vừa chọn xong, đã Escape) thì Enter không
            // làm gì, kể cả không hẹn chọn sau.
            if (!showList) return
            if (!coords && !settled && !navigatedRef.current) {
                pendingEnterRef.current = true
                return
            }
            const option = options[active] ?? options[0]
            if (option) choose(option)
        }
    }

    const message =
        failure === 'error'
            ? m.map_search_error()
            : failure === 'no_results'
              ? m.map_search_no_results()
              : null

    return (
        <>
            {/* LEFT_TOP = dòng riêng ngay dưới nút Bản đồ/Vệ tinh (TOP_LEFT xếp
                ngang, khung hẹp sẽ đè nút toàn màn hình). */}
            <MapControl position={ControlPosition.LEFT_TOP}>
                <div
                    data-testid="map-place-search"
                    className="relative mx-2.5 mt-1 font-sans"
                    style={{ width }}
                >
                    <div className="flex h-10 items-center gap-2 rounded-lg bg-background px-3 text-foreground shadow-md">
                        {predictions.status === 'loading' ? (
                            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                            <Search className="size-4 shrink-0 text-muted-foreground" />
                        )}
                        <input
                            type="text"
                            role="combobox"
                            aria-label={m.map_search_label()}
                            aria-expanded={showList}
                            aria-controls={listId}
                            aria-autocomplete="list"
                            autoComplete="off"
                            enterKeyHint="search"
                            placeholder={m.map_search_placeholder()}
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value)
                                setOpen(true)
                                setActive(0)
                                setDetailError(false)
                                pendingEnterRef.current = false
                                navigatedRef.current = false
                            }}
                            onFocus={() => setOpen(true)}
                            onBlur={() => {
                                pendingEnterRef.current = false
                                setOpen(false)
                            }}
                            onKeyDown={onKeyDown}
                            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
                        />
                        {(query !== '' || pin) && (
                            <button
                                type="button"
                                onClick={clear}
                                aria-label={m.map_search_clear()}
                                className="-mr-1 rounded p-1 text-muted-foreground hover:bg-accent"
                            >
                                <X className="size-4" />
                            </button>
                        )}
                    </div>
                    {showList && (
                        <PlaceSearchOptions
                            id={listId}
                            options={options}
                            active={active}
                            message={message}
                            onActive={setActive}
                            onChoose={choose}
                        />
                    )}
                    {!showList && detailError && (
                        <div
                            role="alert"
                            className="mt-1 rounded-lg bg-background px-3 py-2 text-sm text-destructive shadow-lg"
                        >
                            {m.map_search_error()}
                        </div>
                    )}
                </div>
            </MapControl>
            {pin && (
                <AdvancedMarker position={pin} zIndex={1000}>
                    <Pin
                        background="#2563eb"
                        borderColor="#1e3a8a"
                        glyphColor="#ffffff"
                    />
                </AdvancedMarker>
            )}
        </>
    )
}
